/**
 * Cliente único do Supabase para o app (F1.2).
 *
 * Duas decisões que valem para o produto inteiro:
 *  1. **O aparelho tem identidade própria** (`x-dispositivo-id`): um UUID gerado na primeira abertura e
 *     guardado no aparelho. É por ele que a revogação funciona — sem isso, "revogar o celular do
 *     técnico" não teria como ser cumprido pelo servidor.
 *  2. **Sessão persistida e renovada sozinha**: o técnico não pode ser deslogado no meio de um
 *     checklist; a expiração do token não pode virar trabalho perdido.
 */
import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const CHAVE_DISPOSITIVO = 'agrobox.dispositivo_id';

export function idDoDispositivo(): string {
  const guardado = localStorage.getItem(CHAVE_DISPOSITIVO);
  if (guardado) return guardado;
  const novo = crypto.randomUUID();
  localStorage.setItem(CHAVE_DISPOSITIVO, novo);
  return novo;
}

let cliente: SupabaseClient | null = null;

export function supabase(): SupabaseClient {
  if (cliente) return cliente;
  const url = import.meta.env['VITE_SUPABASE_URL'] as string | undefined;
  const chave = import.meta.env['VITE_SUPABASE_ANON_KEY'] as string | undefined;
  if (!url || !chave) {
    throw new Error(
      'Configuração ausente: defina VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY no ambiente do app.',
    );
  }
  cliente = createClient(url, chave, {
    auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: false },
    global: { headers: { 'x-dispositivo-id': idDoDispositivo() } },
    db: { schema: 'public' },
  });
  return cliente;
}

/** Nível de garantia da sessão: `aal2` = passou pelo segundo fator. Ações de Admin exigem aal2. */
export async function nivelDeGarantia(): Promise<'aal1' | 'aal2' | 'nenhum'> {
  const { data } = await supabase().auth.mfa.getAuthenticatorAssuranceLevel();
  if (!data?.currentLevel) return 'nenhum';
  return data.currentLevel === 'aal2' ? 'aal2' : 'aal1';
}
